





















import { createPopper, Modifier, OptionsGeneric } from '@popperjs/core';
import maxSize from 'popper-max-size-modifier';
import Vue from 'vue';
import { MediaType } from '../../plugins/media/media';
import _ from '../../utils';
import interactable from '../../mixins/interactable/interactable';
import mixins from '../../mixins';

export default mixins(interactable).extend({
  mixins: [interactable],
  props: {
    value: { default: false, type: Boolean },
    openOnClick: { default: true, type: Boolean },
    closeOnScroll: { default: true, type: Boolean },
    closeOnResize: { default: true, type: Boolean },
    referenceElement: { default: undefined, type: Function },
    minWidthOfReference: { default: false, type: Boolean },
    elevation: { default: 3, type: [Number, String] },
    noCloseOnScroll: { default: false, type: Boolean },
    top: { default: false, type: Boolean },
    right: { default: false, type: Boolean },
    bottom: { default: false, type: Boolean },
    left: { default: false, type: Boolean },
    autoStart: { default: false, type: Boolean },
    autoEnd: { default: false, type: Boolean },
  },
  data: () => ({
    popperInstance: undefined as ReturnType<typeof createPopper> | undefined,
    noCloseCooldown: false,
    styleList: {
      minWidth: undefined,
      maxWidth: undefined,
      maxHeight: undefined,
      minHeight: undefined,
    } as Partial<HTMLElement['style']>,
  }),
  computed: {
    contentClassList(): Record<string, boolean> {
      return {
        'p-menu-content-wrapper': true,
        [`elevation-${this.elevation}`]: true,
        'p-menu-modal': this.$media.isMobile,
        'expand-left': this.left && !(this.top || this.bottom),
        'expand-right': this.right && !(this.top || this.bottom) && !this.left,
        'expand-bottom': this.bottom && !(this.right || this.left),
        'expand-top': this.top && !(this.right || this.left),
        'expand-top-left': this.top && this.left && !this.right,
        'expand-top-right': this.top && this.right && !this.left,
        'expand-bottom-left': this.bottom && this.left && !this.right,
        'expand-bottom-right': this.bottom && this.right && !this.left,
        expanded: !!this.value,
      };
    },
  },
  mounted() {
    this.$media.addResizeListener(this.onResize);
  },
  beforeDestroy() {
    this.$media.removeResizeListener(this.onResize);
    this.destroyPopperjs();
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        if (this.value) {
          this.noCloseCooldown = true;
          setTimeout(() => {
            this.noCloseCooldown = false;
          }, 150);
          this.resizeMenu();
          window.addEventListener('scroll', this.onScroll, true);
        } else {
          window.removeEventListener('scroll', this.onScroll);
        }
      },
    },
  },
  methods: {
    onResize(mediaType: MediaType) {
      if (mediaType === 'mobile') {
        this.destroyPopperjs();
      } else if (!this.popperInstance) {
        this.initializePopperjs();
      }
    },
    /** The menu attaches itself to the reference element */
    getReferenceElement() {
      let referenceEl = this.$refs.activator as HTMLElement;
      if (typeof this.referenceElement === 'function') {
        referenceEl = this.referenceElement();
      }
      return referenceEl;
    },
    onScroll(event: Event) {
      if (this.noCloseOnScroll) {
        return;
      }
      const { target } = event;
      const menu = this.$refs.menu as Vue;
      const menuEl = menu?.$el;
      if (!menuEl || !menuEl.contains(target as Node)) {
        this.closeMenu();
      }
    },
    onClickOutside(event: any) {
      const activator = this.$refs.activator as HTMLElement;
      const isInside = activator.contains(event.target);
      if (!isInside) {
        this.closeMenu();
      }
    },
    closeMenu() {
      if (!this.noCloseCooldown) {
        this.$emit('input', false);
      }
    },
    openMenu() {
      this.$emit('input', true);
    },
    onActivatorClicked() {
      if (this.openOnClick) {
        this.openMenu();
      }
    },
    resizeMenu() {
      if (this.$media.isMobile) {
        return;
      }
      this.$nextTick(() => {
        const referenceEl = this.getReferenceElement();
        const appEl = _.component.getAppEl();
        const appElRect = appEl?.getBoundingClientRect();
        if (appElRect) {
          const sideMargin = 32;
          this.styleList.maxHeight = `${appElRect.height - sideMargin}px`;
          this.styleList.maxWidth = `${appElRect.width - sideMargin}px`;
        }
        if (referenceEl && this.minWidthOfReference) {
          if (this.top || this.bottom) {
            this.styleList.minWidth = `${referenceEl.clientWidth}px`;
          }
        }
        this.$nextTick(() => {
          this.popperInstance?.update();
        });
      });
      this.popperInstance?.update();
    },
    destroyPopperjs() {
      // Check destroy function because Popper might be destroying itself already and then the popperInstance is an empty object
      if (this.popperInstance && typeof this.popperInstance.destroy === 'function') {
        this.popperInstance.state.reset = true;
        this.popperInstance.destroy();
        this.popperInstance.forceUpdate();
        this.popperInstance = undefined;
      }
    },
    initializePopperjs() {
      this.popperInstance = {} as any;
      let placement = 'auto' as OptionsGeneric<any>['placement'];
      if (this.autoEnd) {
        placement = 'auto-end';
      }
      if (this.autoStart) {
        placement = 'auto-start';
      }
      if (this.bottom) {
        placement = 'bottom';
      }
      if (this.top) {
        placement = 'top';
      }
      if (this.left) {
        placement = 'left-start';
      }
      if (this.right) {
        placement = 'right-start';
      }
      if (this.bottom && this.left) {
        placement = 'bottom-end';
      }
      if (this.bottom && this.right) {
        placement = 'bottom-start';
      }
      if (this.top && this.left) {
        placement = 'top-end';
      }
      if (this.top && this.right) {
        placement = 'top-start';
      }

      // Wait a tick so the reference-element is surely accessible
      this.$nextTick(() => {
        const appEl = _.component.getAppEl();
        const referenceEl = this.getReferenceElement();
        const menuNode = this.$refs.menu as any;
        if (!menuNode) {
          return;
        }
        const padding = 24;
        const self = this;
        const applyMaxSize = {
          name: 'applyMaxSize',
          enabled: true,
          phase: 'beforeWrite',
          requires: ['maxSize'],
          fn({ state }): void {
            const { height } = state.modifiersData.maxSize;
            const computedHeight = height - padding;
            self.styleList.maxHeight = computedHeight < padding ? undefined : `${computedHeight}px`;
          },
        } as Partial<Modifier<any, any>>;
        const menuEl = menuNode.$el as HTMLElement;
        this.popperInstance = createPopper(referenceEl, menuEl, {
          placement,
          modifiers: [
            // maxSize,
            // applyMaxSize,
            {
              name: 'preventOverflow',
              options: {
                padding,
                boundary: appEl,
                tether: false,
                // TODO: Set altAxis to false after a max-width and max-height have been calculated
                // Or give the option to switch behaviour as a prop
                // More info: https://popper.js.org/docs/v2/modifiers/prevent-overflow/#altaxis
                altAxis: true,
              },
            },
          ],
        });
        this.$nextTick(() => {
          this.popperInstance?.forceUpdate();
          this.resizeMenu();
        });
      });
    },
  },
});
